import React, { useEffect, useState } from 'react';
import { createStructuredSelector } from 'reselect';
import { connect } from 'react-redux';
import { Link, withRouter } from 'react-router-dom';
import { Upload, Printer } from 'react-feather';
import fileDownload from 'js-file-download';
import {
  DatePicker,
  DatePickerInput,
  Select,
  SelectItem,
} from 'carbon-components-react';

import MainHeader from '../../../components/main-header/main-header.component';
import Mainmenu from '../../../components/menu/main-menu/main-menu.component';
import CustomButton from '../../../components/custom-button/custom-btn.component';
import Footer from '../../../components/footer/footer.component';

import MainLoaderModal from '../../../components/modal/main-loader/main-loader.component';
import TransactionReportModal from '../../../components/modal/transaction-report-printout-modal/transaction-report.modal';

import { selectCurrentUser } from '../../../redux/selectors/user.selectors';
import { selectTransactionsReport } from '../../../redux/analytics/analytics.selectors';
import {
  selectLedgerAccounts,
  selectNonExpenseAndRevenueLedgerAccounts,
} from '../../../redux/ledger/ledger.selectors';

import {
  formatCurrency,
  formatDate,
  getQueryString,
} from '../../../utils/common.util';

import './transactions-report.styles.scss';
import { fetchService } from '../../../api/common';
import useSWR from 'swr';

const TransactionsReportPage = ({
  ledgerAccounts,
  nonExpenseAndRevenueLedgerAccounts,
  fetchLedgerAccounts,
  currentUser: { authToken, school },
}) => {
  const { data: academicYears } = useSWR('academic-year/list');
  const { data: terms } = useSWR('academic-year/terms/?academicYearId=-1');

  const [openPrintOutModal, setOpenPrintOutModal] = useState(false);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [ledger, setLedger] = useState('');
  const [academicYearId, setAcademicYearId] = useState('');
  const [termId, setTermId] = useState('');
  const [status, setStatus] = useState('');

  const [openExportLoaderModal, setOpenExportLoaderModal] = useState(false);
  const [exportLoaderModalErrorMessage, setExportLoaderModalErrorMessage] =
    useState(null);

  const buildUrl = (isExporting = false) =>
    `analytics/journal-report${isExporting ? '/export' : ''}?${getQueryString({
      dateFrom,
      dateTo,
      ledgerId: ledger,
      termId,
      academicYearId,
      status,
    })}`;

  const [isReady, setIsReady] = useState(false);
  const { data, error, mutate, isLoading } = useSWR(isReady && buildUrl());
  const [openLoaderModal, setOpenLoaderModal] = useState(isLoading);

  const academicYearOrDateRange = academicYearId || (dateFrom && dateTo);
  useEffect(() => {
    if (!academicYearOrDateRange) {
      setIsReady(false);
    }
  }, [academicYearOrDateRange]);

  if (!ledgerAccounts.fetching && ledgerAccounts.data.length === 0) {
    fetchLedgerAccounts(authToken);
  }

  let ledgerAccount = nonExpenseAndRevenueLedgerAccounts.find(
    (i) => i.id == ledger,
  );

  const filterTexts = [
    {
      label: 'Academic Year',
      text:
        academicYears?.find((ac) => ac.id == academicYearId)?.description ||
        'All',
    },
    {
      label: 'Term',
      text: terms?.find((t) => t.id == termId)?.description || 'All',
    },
    {
      label: 'Start Date',
      text: dateFrom ? formatDate(new Date(dateFrom)) : '-',
    },
    {
      label: 'End Date',
      text: dateTo ? formatDate(new Date(dateTo)) : '-',
    },
    {
      label: 'Ledger Account',
      text: ledgerAccount ? ledgerAccount.description : 'ALL',
    },
    {
      label: 'Status',
      text: status || 'ALL',
    },
  ];

  const exportReport = async () => {
    setExportLoaderModalErrorMessage(null);
    setOpenExportLoaderModal(true);

    const response = await fetchService(buildUrl(true), authToken);

    if (response.success) {
      setOpenExportLoaderModal(false);
      fileDownload(
        response.data.data,
        `journal-voucher-report-${new Date()}.xlsx`,
      );

      return;
    }

    setExportLoaderModalErrorMessage(response.message);
  };

  return (
    <div className="no-print">
      <MainHeader />

      <MainLoaderModal
        openModal={openExportLoaderModal}
        description="Exporting Journals Voucher Report"
        errorMessage={exportLoaderModalErrorMessage}
        tryAgainAction={exportReport}
        cancelAction={() => setOpenExportLoaderModal(false)}
      />

      <MainLoaderModal
        openModal={isLoading}
        description={'Generating report...'}
        errorMessage={error?.message || ''}
        tryAgainAction={() => {
          setOpenLoaderModal(true);
          mutate();
        }}
        cancelAction={() => setOpenLoaderModal(false)}
      />

      <TransactionReportModal
        openModal={openPrintOutModal}
        data={data}
        school={school}
        filterTexts={filterTexts}
        closeAction={() => setOpenPrintOutModal(false)}
      />

      <span className="right">
        <Mainmenu />
      </span>

      <div className="transactions-report-page page-container">
        <div>
          <p>
            <span className="text-12 font-bold text-grey">You are here</span>
            &nbsp;&nbsp;
            <Link to="/analytics" className="text-12 text-dark">
              Analytics
            </Link>
            &nbsp;<span className="text-12">&gt;</span>&nbsp;
            <span className="text-12 text-primary font-bold">
              Journal Voucher Report
            </span>
          </p>
        </div>

        <hr className="grey-hr mt-20" />

        <div className="h-24 uppercase mt-10">
          Journal Voucher Reports{' '}
          {ledgerAccount && `on ${ledgerAccount.description}`} &nbsp;:&nbsp;
          {dateFrom &&
            dateTo &&
            `${formatDate(new Date(dateFrom))} - ${formatDate(
              new Date(dateTo),
            )}`}
        </div>

        {/* filters */}
        <div className="mt-20">
          <div className="bx--row mt-20">
            {/* academic year */}
            <div className="bx--col-md-2 npr mb-20">
              <Select
                labelText="Academic Year"
                onChange={(e) => {
                  setAcademicYearId(e.target.value);
                  setTermId('');
                }}>
                <SelectItem
                  disabled
                  selected
                  value=""
                  text="Select Academic Year"
                />
                <SelectItem value="" text="All" />
                {academicYears?.map((year) => (
                  <SelectItem
                    value={year.id}
                    text={year.description}
                    selected={academicYearId === year.id}
                  />
                ))}
              </Select>
            </div>
            {/* terms */}
            <div className="bx--col-md-2 npr mb-20">
              <Select
                disabled={!academicYearId}
                labelText="Term"
                onChange={(e) => setTermId(e.target.value)}>
                <SelectItem disabled selected value="" text="Select Term" />
                <SelectItem value="" text="All" />
                {terms
                  ?.filter((m) => m.academicYearId == academicYearId)
                  ?.map((term) => (
                    <SelectItem
                      value={term.id}
                      text={term.description}
                      selected={termId === term.id}
                    />
                  ))}
              </Select>
            </div>
            {/* start date */}
            <div className="bx--col-md-2">
              <DatePicker
                dateFormat="Y-m-d"
                datePickerType="single"
                id="start-date-picker"
                onClose={(value) => {
                  if (value.length > 0) {
                    setDateFrom(
                      [
                        value[0].getFullYear(),
                        ('0' + (value[0].getMonth() + 1)).slice(-2),
                        ('0' + value[0].getDate()).slice(-2),
                      ].join('-'),
                    );
                  }
                }}>
                <DatePickerInput
                  className="start-date"
                  id="start-date"
                  name="startDate"
                  labelText="Start date"
                  value={dateFrom}
                  pattern="d{1,2}/d{4}"
                  placeholder="YYYY-MM-DD"
                  type="text"
                />
              </DatePicker>
            </div>
            {/* end date */}
            <div className="bx--col-md-2 npl">
              <DatePicker
                dateFormat="Y-m-d"
                datePickerType="single"
                id="end-date-picker"
                onClose={(value) => {
                  if (value.length > 0) {
                    setDateTo(
                      [
                        value[0].getFullYear(),
                        ('0' + (value[0].getMonth() + 1)).slice(-2),
                        ('0' + value[0].getDate()).slice(-2),
                      ].join('-'),
                    );
                  }
                }}>
                <DatePickerInput
                  className="end-date"
                  id="end-date"
                  name="endDate"
                  labelText="End date"
                  value={dateTo}
                  pattern="d{1,2}/d{4}"
                  placeholder="YYYY-MM-DD"
                  type="text"
                />
              </DatePicker>
            </div>
            <div className="bx--col-md-2 npr">
              <Select
                labelText="Ledger"
                onChange={(e) => setLedger(e.target.value)}>
                <SelectItem disabled selected value="" text="Ledger" />
                <SelectItem value="" text="All" />
                {nonExpenseAndRevenueLedgerAccounts.map((account) => (
                  <SelectItem value={account.id} text={account.description} />
                ))}
              </Select>
            </div>
            {/* status */}
            <div className="bx--col-md-2 npr  mb-20">
              <Select
                labelText="Status"
                onChange={(e) => setStatus(e.target.value)}>
                <SelectItem selected value="" text="All" />
                <SelectItem text={'Draft'} value={'Draft'} />
                <SelectItem text={'Submitted'} value={'Submitted'} />
                <SelectItem text={'Voided'} value={'Voided'} />
                <SelectItem text={'Approved'} value={'Approved'} />
              </Select>
            </div>

            <div className="bx--col-md-2">
              <CustomButton
                classes="f-width primary mt-25"
                disabled={!academicYearOrDateRange}
                onClick={() => setIsReady(true)}>
                RUN REPORT
              </CustomButton>
            </div>
          </div>
        </div>

        {!academicYearOrDateRange && (
          <div className="mt-40 text-center font-500 text-danger text-20">
            You must provide either an academic year or a duration (a Start Date
            and an End Date)
          </div>
        )}

        {data && data?.length > 0 && (
          <div className="mt-20">
            <table className="bx--data-table table-white">
              <thead>
                <tr>
                  <th>Date Created</th>
                  <th>Tranaction Date</th>
                  <th>JE Number</th>
                  <th>Reference Number</th>
                  <th>Memo</th>
                  <th>Debit</th>
                  <th>Credit</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {data.flatMap((entry) =>
                  entry.lines.map((line, index) => {
                    return (
                      <tr key={`${entry.id}-${index}`}>
                        <td>{formatDate(entry.createdDateTime)}</td>
                        <td>{formatDate(entry.transactionDate)}</td>
                        <td>{entry.documentNumber}</td>
                        <td>{entry.referenceNumber}</td>
                        <td>{line.memo || entry.memo}</td>
                        <td>{formatCurrency(line.debitAmount)}</td>
                        <td>{formatCurrency(line.creditAmount)}</td>
                        <td>{entry.status}</td>
                      </tr>
                    );
                  }),
                )}
              </tbody>
            </table>

            <div className="bx--row mt-35">
              <div className="bx--col-lg-2">
                <CustomButton
                  classes="secondary f-width font-bold"
                  onClick={exportReport}>
                  <Upload size="11" strokeWidth="5px" />
                  &nbsp; EXPORT TO EXCEL
                </CustomButton>
              </div>
              <div className="bx--col-lg-2">
                <CustomButton
                  classes="primary f-width font-bold"
                  onClick={() => setOpenPrintOutModal(true)}>
                  <Printer size="11" strokeWidth="5px" /> &nbsp; PRINT
                </CustomButton>
              </div>
              <div className="bx--col-lg-8"></div>
            </div>
          </div>
        )}
        {data && data?.length === 0 && (
          <div className="mt-40">
            <p className="text-12 text-center font-500 text-20">
              No records found for the selected filters.
            </p>
          </div>
        )}
        <Footer fixed={false} showLinks={false} />
      </div>
    </div>
  );
};

const mapStateToProps = createStructuredSelector({
  transactionsReport: selectTransactionsReport,
  ledgerAccounts: selectLedgerAccounts,
  nonExpenseAndRevenueLedgerAccounts: selectNonExpenseAndRevenueLedgerAccounts,
  currentUser: selectCurrentUser,
});

const mapDispatchToProps = (dispatch) => ({
  fetchTransactionsReport: (dateFrom, dateTo, ledger, authToken) =>
    dispatch({
      type: 'FETCH_TRANSACTIONS_REPORT_REQUESTED',
      payload: { dateFrom, dateTo, authToken, ledger },
    }),
  fetchLedgerAccounts: (authToken) =>
    dispatch({
      type: 'FETCH_LEDGER_ACCOUNTS_REQUESTED',
      payload: { authToken },
    }),
});

export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(TransactionsReportPage),
);
